import React, { useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import { Badge, Button, Icon, useToast } from "stories/Admin";
import { AUDIO_ACCEPT, audioFileError, errorText, KIDS_API } from "./kidsTypes";
import styles from "./KidsPage.module.scss";

interface Props {
  label: string;
  /** Barnets egen lydfil (tom = ingen). */
  value: string;
  onChange: (url: string) => void;
  /** Mappen i lagringen (stasjonens id). */
  itemId: string;
  /** Lyden som brukes når barnet ikke har egen lyd (f.eks. duaens). */
  fallbackUrl?: string | null;
  fallbackLabel?: string;
  /** Ekstra knapper, f.eks. «Lag egen barnekopi av lyden». */
  actions?: React.ReactNode;
  compact?: boolean;
}

/**
 * Lyd for en stasjon eller en adhan-frase: spiller av, laster opp en
 * barnevennlig fil (mp3/m4a/aac, maks 10 MB) og fjerner barnets egen lyd.
 * Opplastingen lagres først når stasjonen lagres.
 */
const KidsAudioField: React.FC<Props> = ({
  label,
  value,
  onChange,
  itemId,
  fallbackUrl,
  fallbackLabel,
  actions,
  compact,
}) => {
  const upload = useFetch<{ url: string }>();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const own = value.trim();
  const playing = own || fallbackUrl || "";

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const problem = audioFileError(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await upload.execute(`${KIDS_API}/audio`, "POST", form, {
        itemId: itemId || undefined,
      });
      if (result?.url) {
        onChange(result.url);
        toast.info("Lyden er lastet opp. Trykk «Lagre» for å bruke den.");
      } else {
        toast.error(
          errorText(upload.lastError(), "Kunne ikke laste opp lyden."),
        );
      }
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className={compact ? styles.audioCompact : styles.audio}>
      <div className={styles.audioHead}>
        <span className={styles.audioLabel}>{label}</span>
        {own ? (
          <Badge tone="mint">Barnets egen lyd</Badge>
        ) : fallbackUrl ? (
          <Badge tone="grey">{fallbackLabel ?? "Standardlyd"}</Badge>
        ) : (
          <Badge tone="gold">Ingen lyd</Badge>
        )}
      </div>
      {playing && (
        <audio
          className={styles.player}
          controls
          preload="none"
          src={playing}
          aria-label={`${label}: avspilling`}
        />
      )}
      <div className={styles.audioActions}>
        <input
          ref={input}
          type="file"
          accept={AUDIO_ACCEPT}
          hidden
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={busy}
          icon={<Icon name="upload" size={14} />}
          onClick={() => input.current?.click()}
        >
          {own ? "Bytt lyd" : "Last opp lyd"}
        </Button>
        {own && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            icon={<Icon name="x" size={14} />}
            onClick={() => onChange("")}
          >
            Fjern egen lyd
          </Button>
        )}
        {actions}
      </div>
    </div>
  );
};

export default KidsAudioField;
